/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import UserTable, { MgmtInvitation, MgmtUser, UserTableUser } from './UserTable';
import UserInvitationForm from './UserInvitationForm';
import Loader from '../../components/loader';
import NotAuthorised from '../../routes/not-authorised';
import { getInitiativeUsers } from '../../actions/initiative';
import { generateUrl } from '../util';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../components/dashboard';
import UserInvitationFormContainer from './UserInvitationFormContainer';
import { AdminBreadcrumbs } from '../admin-dashboard/AdminBreadcrumbs';
import { UserPermissions } from '../../constants/users';
import { filterValidRoles, UserRoles } from '../../constants/user';
import './styles.scss';
import { getFullName } from '../../utils/user';
import { OnboardingStatus } from '../../types/onboarding';
import { BasicAlert } from '../../components/alert/BasicAlert';
import { RouteInterfaceMin } from '../../types/routes';
import { UserLimitUsage } from '../../apps/company-tracker/components/user-limit/UserLimitTypes';
import { BulkOnboardingImportContainer } from './BulkOnboardingImportContainer';
import { Button } from 'reactstrap';

interface UserStateInterface {
  data: {
    users: MgmtUser[],
    parentInitiativeIds: string[],
    invitations: MgmtInvitation[]
  };
  loaded: boolean
}

const isInherited = (userPermissions: UserPermissions[], initiativeId: string): boolean => {
  return !userPermissions.find(p => p.initiativeId === initiativeId);
}

const currentLevelPermission = (userPermissions: UserPermissions[], parentInitiativeIds: string[], initiativeId: string): UserRoles[] => {
  const permissions = userPermissions.find(p => p.initiativeId === initiativeId);
  if (permissions) {
    return permissions.permissions.filter(filterValidRoles);
  }

  const parentPermissionId = parentInitiativeIds.find(id => userPermissions.find(p => p.initiativeId === id));
  const parentPermission = parentPermissionId ? userPermissions.find(p => p.initiativeId === parentPermissionId) : undefined;
  if (parentPermission) {
    return parentPermission.permissions.filter(filterValidRoles);
  }
  return [];
}

const getConsolidateUsers = (usersState: UserStateInterface, initiativeId: string): UserTableUser[] => {
  const userTableUsers: UserTableUser[] = [];

  if (!usersState.loaded) {
    return userTableUsers;
  }

  usersState.data.users.forEach(user => {
    userTableUsers.push({
      _id: user._id,
      isStaff: user.isStaff ?? false,
      isActivated: true,
      name: getFullName(user, user._id),
      email: user.email,
      roles: currentLevelPermission(user.permissions, usersState.data.parentInitiativeIds, initiativeId),
      lastUpdated: user.lastLogin,
      lastLogin: user.lastLogin,
      isInherited: isInherited(user.permissions, initiativeId),
      isRequestToJoin: false,
      user
    });
  })

  usersState.data.invitations.forEach(invitation => {
    const isRequestToJoin = invitation.status === OnboardingStatus.NotStarted;
    const user = invitation.user;
    const emailPrefix = user.email.split('@')[0];
    userTableUsers.push({
      _id: invitation._id,
      isStaff: false,
      isActivated: false,
      name: isRequestToJoin ? getFullName(user, emailPrefix) : emailPrefix,
      email: user.email,
      roles: currentLevelPermission(user.permissions, usersState.data.parentInitiativeIds, initiativeId),
      lastUpdated: invitation.created,
      lastLogin: '',
      isInherited: isInherited(user.permissions, initiativeId),
      isRequestToJoin,
      invitation
    });
  })

  return userTableUsers;
}

interface UserManagementProps {
  initiativeId: string;
  page?: string;
  userLimit: number;
  UserLimitComponent?: React.ElementType<UserLimitUsage>;
  /** @deprecated */
  isPortfolioTracker?: boolean;
  baseRoute: RouteInterfaceMin;
  buttons?: React.JSX.Element[];
  canInviteMultipleUsers?: boolean;
}

interface UserLimitUpgradeParams extends Pick<UserManagementProps, 'UserLimitComponent' | 'userLimit'> {
  limitReached: boolean;
  currentUsage: number;
}

const UserLimitUpgrade = ({ limitReached, currentUsage, UserLimitComponent, userLimit }: UserLimitUpgradeParams) => {
  if (!limitReached) {
    return null;
  }

  if (UserLimitComponent) {
    return <UserLimitComponent currentUsage={currentUsage} userLimit={userLimit}/>;
  }

  return (
    <BasicAlert type='warning'>
      You have reached your user limit of {userLimit}. Please contact us to increase your limit.
    </BasicAlert>
  );
};

export const UserManagement = (props: UserManagementProps) => {
  const { userLimit, initiativeId, page, baseRoute, UserLimitComponent, isPortfolioTracker, canInviteMultipleUsers = false } = props;

  const history = useHistory();

  const [notAuthorised, setNotAuthorised] = React.useState(false);
  const [usersState, setUsersState] = React.useState<UserStateInterface>({ data: { users: [], parentInitiativeIds: [], invitations: [] }, loaded: false });

  const listUsers = useMemo(() => {
    return getConsolidateUsers(usersState, initiativeId).reduce(
      (accumulator, user) => {
        if (user.isStaff) {
          accumulator.staff.push(user);
          return accumulator;
        }
        accumulator.users.push(user);
        return accumulator;
      },
      { users: [], staff: [] } as { users: UserTableUser[]; staff: UserTableUser[] }
    );
  }, [usersState, initiativeId]);

  React.useEffect(() => {
    if (!initiativeId) {
      return;
    }
    getInitiativeUsers(initiativeId)
      .then((data) => setUsersState({ data: data, loaded: true }))
      .catch(() => {
        setNotAuthorised(true);
        setUsersState((prev) => ({ ...prev, loaded: true }));
      });
  }, [initiativeId]);

  if (!initiativeId || !usersState.loaded) {
    return <Loader />;
  }

  if (notAuthorised) {
    return <NotAuthorised />
  }

  const handleReload = async () => {
    try {
      await getInitiativeUsers(initiativeId).then(data => setUsersState({ data: data, loaded: true }));
    } catch (e) {
      setNotAuthorised(true);
    }
  }

  const nonStaffUserCount = listUsers.users.length;
  const limitReached = userLimit > 0 && nonStaffUserCount >= userLimit;

  const toggleUserInvitationForm = () => {
    const invitationUrl = generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId, page: 'invite' });
    history.push(invitationUrl);
  }

  const onClickInviteMultipleUsers = () => {
    const url = generateUrl(baseRoute, { initiativeId, page: 'import-export' });
    return history.push(url);
  }

  const baseBreadcrumb = {
    label: 'Manage Users',
    url: generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId }),
  };

  if (page === 'invite') {
    return (
      <Dashboard className='userManagement'>
        <DashboardRow>
          <AdminBreadcrumbs
            initiativeId={initiativeId}
            breadcrumbs={[baseBreadcrumb, { label: 'Invite user' }]}
            isPortfolioTracker={isPortfolioTracker}/>
        </DashboardRow>
        <DashboardSectionTitle title={'Invite user(s)'} buttons={props.buttons} />
        <DashboardSection className='userManagement-container' >
          <UserInvitationFormContainer>
            <UserInvitationForm initiativeId={initiativeId} handleReload={handleReload} />
          </UserInvitationFormContainer>
        </DashboardSection>
      </Dashboard>
    );
  }

  if (page === 'import-export' && canInviteMultipleUsers) {
    return <BulkOnboardingImportContainer initiativeId={initiativeId} userLimit={userLimit} />
  }
  
  const renderManageUserButtons = () => {
    if (!canInviteMultipleUsers) {
      return props.buttons;
    }

    const inviteButton = (
      <Button onClick={onClickInviteMultipleUsers} disabled={limitReached}>
        <i className='fal fa-users-medical mr-2' />
        Invite multiple users across your reporting levels
      </Button>
    );

    if (!props.buttons) {
      return [inviteButton];
    }

    return [...props.buttons, inviteButton];
  };

  return (
    <Dashboard className='userManagement'>
      <DashboardRow>
        <AdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[baseBreadcrumb]}
          isPortfolioTracker={isPortfolioTracker}/>
      </DashboardRow>
      <DashboardSectionTitle title={'Manage users'} buttons={renderManageUserButtons()} />
      <DashboardSection className='userManagement-container'>
        <UserLimitUpgrade
          currentUsage={nonStaffUserCount}
          userLimit={userLimit}
          limitReached={limitReached}
          UserLimitComponent={UserLimitComponent}/>
        <UserTable
          users={listUsers.users}
          handleReload={handleReload}
          toggleUserInvitationForm={toggleUserInvitationForm}
          isAddUserDisabled={limitReached}
        />
        {!isPortfolioTracker ?
          <div className='mt-3 text-right'>
            {nonStaffUserCount}/{userLimit || 'Unlimited'} users
          </div>
        : null}
      </DashboardSection>
      <DashboardSectionTitle title={'Support users (G17Eco staff)'} />
      <DashboardSection className='userManagement-container'>
        <UserTable
          users={listUsers.staff}
          handleReload={handleReload}
          toggleUserInvitationForm={toggleUserInvitationForm}
          btnText='Add support user'
        />
      </DashboardSection>
    </Dashboard>
  )
}
